import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote, CheckCircle2, Sparkles, Users, Building2 } from 'lucide-react';

export const AboutPage = () => {
  const beliefs = [
    { num: '01', text: 'Clarity sells. When your message is easy to understand, it\'s easier to trust and easier to buy.' },
    { num: '02', text: 'Strategy should make execution simpler, not more complicated.' },
    { num: '03', text: 'Buyers trust businesses that say what they do plainly and prove it consistently.' },
    { num: '04', text: 'Most B2B companies aren\'t failing because they\'re not trying. They\'re failing because the system isn\'t giving them a clear enough path forward.' },
  ];

  const primaryAudience = [
    'US early-stage B2B founders',
    'B2B SaaS founders',
    'Founder-led tech businesses',
    'Professional services firms',
    'Consulting firms',
    'Financial services firms',
  ];

  const secondaryAudience = [
    'Marketing leaders in lean B2B teams',
    'Fractional CMOs who need strategic backup',
    'Operators who\'ve outgrown random marketing and need a proper system',
  ];

  return (
    <div data-testid="about-page" className="w-full">
      {/* Hero */}
      <section data-testid="about-hero" className="relative bg-[#0F2A4A] text-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute top-20 -left-32 w-96 h-96 bg-[#3FC9E8]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-20 -right-32 w-96 h-96 bg-[#1A9FE0]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#3FC9E8 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-px bg-[#3FC9E8]" />
              <span className="text-sm uppercase tracking-[0.2em] text-[#3FC9E8] font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>About</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              We exist for businesses that are <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>good at what they do,</span> and finally want their marketing to show it.
            </h1>
            <div className="space-y-4 text-lg text-white/80 leading-relaxed max-w-3xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <p>Most of the companies we work with aren't struggling because they lack ambition or capability. They're struggling because the way they talk about what they do doesn't match the quality of what they actually deliver.</p>
              <p className="text-[#3FC9E8] font-medium">That gap is costing them clients they should be winning. The B2B Digital was built to close it.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="relative bg-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#0F2A4A 1px, transparent 1px), linear-gradient(90deg, #0F2A4A 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>What We Believe</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                A few things we believe, and <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>build everything around.</span>
              </h2>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {beliefs.map((belief, idx) => (
              <motion.div key={belief.num} data-testid={`belief-${idx + 1}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.1 }} viewport={{ once: true, margin: '-50px' }} whileHover={{ y: -4 }} className="bg-[#F7FAFC] p-8 lg:p-10 relative group overflow-hidden">
                <div className="flex items-start gap-6">
                  <span className="text-5xl lg:text-6xl font-bold leading-none flex-shrink-0" style={{ fontFamily: 'Outfit, sans-serif', background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{belief.num}</span>
                  <p className="text-[#0F2A4A] text-lg leading-relaxed pt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{belief.text}</p>
                </div>
                <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500 bg-gradient-to-r from-[#3FC9E8] to-[#1A9FE0]" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="relative bg-[#F7FAFC] px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#0F2A4A 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }} />
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Who We Work With</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                B2B businesses that are serious about growth and <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>done with guessing.</span>
              </h2>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-12 gap-6 lg:gap-8">
            {/* Primary */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true, margin: '-80px' }} className="md:col-span-7 bg-white p-8 lg:p-10 border border-[#5A6B7E]/15" data-testid="audience-primary">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#3FC9E8]/15 border border-[#3FC9E8]/30 flex items-center justify-center">
                  <Building2 className="text-[#3FC9E8]" size={22} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#1A9FE0] font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Primary</p>
                  <h3 className="text-2xl font-bold text-[#0F2A4A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Founders & Firms</h3>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {primaryAudience.map((aud, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-2">
                    <CheckCircle2 className="text-[#3FC9E8] flex-shrink-0" size={16} />
                    <span className="text-[#0F2A4A]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{aud}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Secondary */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }} viewport={{ once: true, margin: '-80px' }} className="md:col-span-5 bg-[#0F2A4A] text-white p-8 lg:p-10" data-testid="audience-secondary">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#3FC9E8]/15 border border-[#3FC9E8]/30 flex items-center justify-center">
                  <Users className="text-[#3FC9E8]" size={22} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#3FC9E8] font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Secondary</p>
                  <h3 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Marketing Leaders</h3>
                </div>
              </div>
              <div className="space-y-3">
                {secondaryAudience.map((aud, idx) => (
                  <div key={idx} className="flex items-start gap-3 py-1">
                    <CheckCircle2 className="text-[#3FC9E8] flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-white/85" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{aud}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }} viewport={{ once: true }} className="mt-12 text-center max-w-2xl mx-auto">
            <p className="text-lg text-[#0F2A4A] font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>If that sounds like you, you're in the right place.</p>
            <p className="text-[#5A6B7E] italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>If you want marketing that looks active but doesn't actually move anything, probably not.</p>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="bg-white px-6 md:px-12 lg:px-24 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="grid md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>What We Do</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                We tighten the parts of marketing that are usually <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>left vague.</span>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-lg text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                That includes positioning, messaging, content direction, website structure, lead generation systems — the foundational work that makes everything else easier to sell, easier to scale, and easier to measure. The goal is to make your business easier to understand and easier to buy from.
              </p>
            </div>
          </motion.div>
          <div className="text-center">
            <Link to="/services" data-testid="about-services-cta" className="inline-flex items-center justify-center bg-[#0F2A4A] text-white font-medium px-8 py-4 hover:bg-[#0A4DA6] transition-all duration-300 active:scale-95">
              Explore all services <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why This Exists */}
      <section className="relative bg-[#0F2A4A] text-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute top-20 -right-32 w-96 h-96 bg-[#3FC9E8]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#3FC9E8]" />
              <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#3FC9E8]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Why This Exists</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Too many good businesses are being held back by <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>weak marketing.</span>
            </h2>
            <div className="space-y-4 text-lg text-white/80 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <p>The product is solid. The service is strong. Your team knows its craft. But the message doesn't land and everyone is expected to somehow keep the pipeline alive on enthusiasm and referrals alone.</p>
              <p className="text-[#3FC9E8] font-medium">That's not a system but a temporary arrangement with better branding.</p>
              <p>We built The B2B Digital to give serious businesses the strategic marketing infrastructure they actually deserve.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Founder Note */}
      <section data-testid="founder-note" className="relative bg-[#F7FAFC] px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#0F2A4A 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }} />
        <div className="max-w-5xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="bg-white p-10 lg:p-16 relative overflow-hidden">
            {/* Decorative quote */}
            <Quote className="absolute top-8 right-8 text-[#3FC9E8]/20" size={64} />

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-px bg-[#3FC9E8]" />
              <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Founder Note</span>
            </div>
            <h2 className="text-3xl sm:text-4xl tracking-tight leading-[1.1] text-[#0F2A4A] font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              From <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Maleeha</span>
            </h2>
            <div className="space-y-4 text-[#5A6B7E] leading-relaxed text-lg" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <p>I built The B2B Digital because I kept seeing the same problem over and over again — smart businesses with real value, but no clear marketing system to support it.</p>
              <p>I know what it means to work inside lean teams, with limited budgets, and founders who need results yesterday. I know the difference between marketing that looks good in a deck and marketing that actually helps the business grow.</p>
              <p className="text-[#0F2A4A] font-semibold">That's what this is for.</p>
              <p>If you're building something serious and want the marketing to finally match it — I'd love for you to be here.</p>
            </div>
            <div className="mt-8 pt-8 border-t border-[#5A6B7E]/15 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#3FC9E8] to-[#1A9FE0] flex items-center justify-center text-white font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>M</div>
              <div>
                <p className="text-lg font-bold text-[#0F2A4A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Maleeha</p>
                <p className="text-sm text-[#5A6B7E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Founder, The B2B Digital</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#3FC9E8] px-6 md:px-12 lg:px-24 py-20 lg:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Want the marketing to match the business?</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="about-cta-primary" className="inline-flex items-center justify-center bg-[#0F2A4A] text-white font-medium px-8 py-4 hover:bg-[#0A4DA6] transition-all duration-300 active:scale-95">
              Book a FREE Strategy Call <ArrowRight className="ml-2" size={20} />
            </Link>
            <Link to="/contact?scorecard=true" data-testid="about-cta-secondary" className="inline-flex items-center justify-center border-2 border-[#0F2A4A] text-[#0F2A4A] bg-transparent hover:bg-[#0F2A4A] hover:text-white font-medium px-8 py-4 transition-all duration-300 active:scale-95">
              Get The Brand Clarity Scorecard
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
